/**
 * 任务卡片组件 - 稳定布局
 * 固定高度避免抖动，优化交互反馈
 */

import React, { useCallback } from "react";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Thermometer,
  Droplets,
  Activity,
  Eye,
  RefreshCw,
  Zap,
} from "lucide-react";
import type { DashboardTask } from "@/types/daily-dashboard";

interface TaskCardProps {
  task: DashboardTask;
  onToggle: (taskId: string, completed: boolean) => void;
  className?: string;
}

/**
 * 触发设备震动反馈
 */
function triggerHaptic(): void {
  if (navigator.vibrate) {
    navigator.vibrate(30);
  }
}

/**
 * 根据任务类型返回对应的图标和配色
 */
function getTaskCategory(task: DashboardTask) {
  const action = task.action.toLowerCase();
  const notes = task.notes?.toLowerCase() || "";
  const combined = action + " " + notes;

  // 温度相关
  if (
    combined.includes("温度") ||
    combined.includes("升温") ||
    combined.includes("降温") ||
    combined.includes("°c") ||
    combined.includes("加热")
  ) {
    return {
      icon: Thermometer,
      bgClass: "bg-red-500/10",
      iconClass: "text-red-500",
      badgeClass: "bg-red-500/10 text-red-600 dark:text-red-400",
    };
  }

  // 换水/药物相关
  if (
    combined.includes("换水") ||
    combined.includes("药") ||
    combined.includes("投放") ||
    combined.includes("ml") ||
    combined.includes("孔雀石") ||
    combined.includes("盐")
  ) {
    return {
      icon: Droplets,
      bgClass: "bg-blue-500/10",
      iconClass: "text-blue-500",
      badgeClass: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
    };
  }

  // 观察相关
  if (
    combined.includes("观察") ||
    combined.includes("检查") ||
    combined.includes("记录") ||
    combined.includes("监测")
  ) {
    return {
      icon: Eye,
      bgClass: "bg-purple-500/10",
      iconClass: "text-purple-500",
      badgeClass: "bg-purple-500/10 text-purple-600 dark:text-purple-400",
    };
  }

  // 清洁/维护
  if (
    combined.includes("清洁") ||
    combined.includes("更换") ||
    combined.includes("维护") ||
    combined.includes("过滤")
  ) {
    return {
      icon: RefreshCw,
      bgClass: "bg-cyan-500/10",
      iconClass: "text-cyan-500",
      badgeClass: "bg-cyan-500/10 text-cyan-600 dark:text-cyan-400",
    };
  }

  // 默认/关键操作
  return {
    icon: task.isCritical ? Zap : Activity,
    bgClass: task.isCritical ? "bg-orange-500/10" : "bg-slate-500/10",
    iconClass: task.isCritical ? "text-orange-500" : "text-slate-500",
    badgeClass: task.isCritical
      ? "bg-orange-500/10 text-orange-600 dark:text-orange-400"
      : "bg-slate-500/10 text-slate-600 dark:text-slate-400",
  };
}

/**
 * 从任务描述中提取参数（如温度、剂量）
 */
function extractParameter(task: DashboardTask): string | null {
  const combined = task.action + " " + (task.notes || "");

  // 匹配温度
  const tempMatch = combined.match(/(\d+)\s*[°℃]?\s*[CcFf]?(?=[^\d]|$)/);
  if (tempMatch) return `${tempMatch[1]}°C`;

  // 匹配剂量
  const doseMatch = combined.match(
    /(\d+\.?\d*)\s*(ml|mL|毫升|g|克|mg|毫克|片)/i,
  );
  if (doseMatch) return `${doseMatch[1]}${doseMatch[2].toLowerCase()}`;

  return null;
}

export function TaskCard({ task, onToggle, className }: TaskCardProps) {
  const handleToggle = useCallback(
    (checked: boolean) => {
      triggerHaptic();
      onToggle(task.id, checked);
    },
    [task.id, onToggle],
  );

  const hasNotes = !!task.notes;
  const category = getTaskCategory(task);
  const IconComponent = category.icon;
  const parameter = extractParameter(task);

  return (
    <div
      className={cn(
        "group relative flex items-start gap-4 p-4 rounded-xl transition-colors duration-150",
        "border",
        // 固定最小高度避免抖动
        "min-h-[72px]",
        task.isCompleted
          ? "bg-muted/10 border-border/30"
          : cn(
              "bg-card/50 border-border/40",
              "hover:border-border/80 hover:bg-accent/5",
            ),
        className,
      )}
    >
      {/* 左侧：类型图标 - 固定尺寸 */}
      <div
        className={cn(
          "flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center",
          "transition-opacity duration-150",
          category.bgClass,
          task.isCompleted && "opacity-40",
        )}
      >
        <IconComponent className={cn("w-5 h-5", category.iconClass)} />
      </div>

      {/* 中间：任务内容 */}
      <div className="flex-1 min-w-0 space-y-1.5">
        <h4
          className={cn(
            "text-sm font-medium leading-snug transition-colors duration-150",
            task.isCompleted && "line-through text-muted-foreground",
          )}
        >
          {task.action}
        </h4>

        {hasNotes && (
          <p
            className={cn(
              "text-xs text-muted-foreground leading-relaxed line-clamp-2",
              task.isCompleted && "opacity-60",
            )}
          >
            {task.notes}
          </p>
        )}
      </div>

      {/* 右侧：参数胶囊 + Checkbox */}
      <div className="flex items-center gap-2.5 shrink-0 self-center">
        {/* 参数胶囊 */}
        {parameter && !task.isCompleted && (
          <span
            className={cn(
              "text-xs font-mono font-semibold px-2 py-1 rounded-md",
              category.badgeClass,
            )}
          >
            {parameter}
          </span>
        )}

        <Checkbox
          id={task.id}
          checked={task.isCompleted}
          onCheckedChange={handleToggle}
          className={cn(
            "w-5 h-5 shrink-0 transition-colors",
            "data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500",
          )}
        />
      </div>
    </div>
  );
}

export default TaskCard;
